// Central bilingual dictionary for the whole site.
// French typography note: \u00A0 is a no-break space (required before ? ! : ; and inside « »).

export const translations = {
  en: {
    nav: {
      philosophy: "Philosophy",
      services: "Services",
      portfolio: "Portfolio",
      faq: "FAQ",
      startProject: "Start a Project",
      status: "First venture in build",
    },
    intro: { lines: ["YOUR NEXT STEP", "IN HUMAN EVOLUTION."] },
    hero: {
      chip: "Multigroupnetwork • Online Venture Studio",
      lines: ["We Build & Operate", "Digital Ventures", "From Zero To Launch"],
      subtext:
        "Have a business idea? Multigroupnetwork builds, launches and operates digital companies from zero to launch. Online venture studio in Toulouse, France.",
      ctaExplore: "Explore Portfolio",
      ctaInitiate: "Initiate Project",
      cardLabel: "Multigroupnetwork / Studio",
      cardTitleBuild: "Venture 01 in build",
      cardTitleLive: "Venture 01 is live",
      cardBodyBuild:
        "SendexPro — our first company — launches in seven months. No inflated numbers here, just work in progress.",
      cardBodyLive:
        "SendexPro — our first company — is published and operating. Slot 02 is in build.",
      yourVenture: "Your venture",
      openSlot: "Open slot",
      comingSoon: "Coming soon",
      liveNow: "Live now",
    },
    marquee: ["Strategy", "Identity Design", "Full-Stack Dev", "Online Publishing", "Cloud Infra", "Growth Management"],
    manifesto: {
      badge: "[ 01 / Philosophy ]",
      heading: "The Multigroupnetwork Operating Model",
      sub: "We are not an agency. We are a management company that builds, publishes, and operates its own portfolio of online companies — and builds yours the same way.",
      chapterLabel: "Chapter",
      chapters: [
        { num: "01", title: "The Zero State", body: "Every venture begins as a signal in the noise. We validate untapped digital markets before a single line of code is written." },
        { num: "02", title: "Precision Architecture", body: "We engineer scalable, cloud-first infrastructure designed to carry a company from its first user to its millionth." },
        { num: "03", title: "Sovereign Launch", body: "Deployment is a discipline. We publish ventures 100% online — no physical overhead, no geographic ceiling." },
        { num: "04", title: "Perpetual Growth", body: "Launch is day one. Multigroupnetwork operates each venture with autonomous systems and metric-driven scaling, indefinitely." },
      ],
    },
    services: {
      badge: "[ 02 / Services ]",
      heading: "Complete Lifecycle Company Building",
      sub: "One partner, five stages. You bring the ambition — Multigroupnetwork delivers a published, operating online company.",
      stageLabel: "Stage",
      steps: [
        { num: "01", title: "Strategy & Market Intelligence", items: ["Market validation", "Competitive mapping", "Revenue model design", "Venture roadmap"] },
        { num: "02", title: "Brand Identity & Product Design", items: ["Naming & wordmark", "Visual identity system", "UX architecture", "Product interface design"] },
        { num: "03", title: "Full-Stack Engineering & AI", items: ["Web platform build", "AI integration", "Cloud infrastructure", "Security & compliance"] },
        { num: "04", title: "Online Publishing & Deployment", items: ["Production deployment", "Domain & DNS setup", "Analytics wiring", "Launch campaign"] },
        { num: "05", title: "Operations & Scaling", items: ["Managed operations", "Growth experiments", "Metric reporting", "Team handover"] },
      ],
      ventureLabel: "Your Venture",
      ctaTitle: "Ready to move from idea to operating company?",
      ctaBody: "Tell us what you want to build. We respond to every enquiry within 48 hours.",
      ctaLink: "Start the conversation",
    },
    portfolio: {
      badge: "[ 03 / Ventures ]",
      heading: "Companies Built & Managed by Multigroupnetwork",
      sub: "Our first venture is launching soon — with more companies in build behind it.",
      projectDesc: "Smart shipping platform that turns travelers' spare luggage space into a global delivery network.",
      tags: ["Crowd Shipping", "Logistics", "Marketplace"],
      comingSoon: "Coming soon",
      launchesIn: " — launches in 7 months",
      liveBadge: "Live now — launched",
      liveStripTitle: "We're live.",
      liveStripNote: "SendexPro is published and operating",
      countdownLabels: ["Days", "Hours", "Min", "Sec"],
      untilLaunch: "Until launch",
      readCase: "Read the case study",
      readCaseAria: "Read the SendexPro case study",
      slotLabel: "Slot",
      haveIdea: "Have an idea?",
      comingSoonPh: "Coming Soon",
      couldBeYou: "This could be you",
      inBuild: "Company in build",
      claimSpot: "Claim your spot",
    },
    faq: {
      badge: "[ 04 / FAQ ]",
      heading: "Clear Answers on Building with Multigroupnetwork",
      sub: "Everything founders usually ask before starting an A-to-Z build. Something else on your mind? Write to us below.",
      items: [
        { q: "How long does it take to build and publish a company with Multigroupnetwork?", a: "Most ventures move from validated idea to published online company in 4 to 8 weeks, depending on scope. Simple SaaS products land closer to 4 weeks; platforms with AI or complex infrastructure typically need 6 to 8." },
        { q: "Do you work on a fee basis or an equity model?", a: "Both. We offer a fixed-fee build model, a hybrid fee-plus-equity partnership, and — for exceptional ideas — a full venture-partnership model where Multigroupnetwork invests the build in exchange for equity. We help you pick the structure that fits." },
        { q: "Who owns the company and the intellectual property?", a: "You do. On fixed-fee engagements, 100% of the IP, code, brand assets, and infrastructure transfer to you at launch. Equity partnerships define shared ownership transparently in the venture agreement before any work begins." },
        { q: "What happens after launch? Can Multigroupnetwork keep operating the company?", a: "Yes — that is what makes Multigroupnetwork a management company rather than an agency. We offer ongoing operations: infrastructure management, growth experiments, metric reporting, and product iteration. You can also take over fully with a structured handover." },
        { q: "What technology stack do you build on?", a: "We choose the stack per venture, not by habit. Typical builds use modern web frameworks, cloud-native infrastructure, and AI integrations where they create real leverage. Everything is documented and built for your team to extend." },
      ],
    },
    contact: {
      badge: "[ 05 / Contact ]",
      heading: "Build Your Next Digital Company with Multigroupnetwork",
      sub: "Tell us about the company you want to build. Every enquiry is read by a partner, and we respond within 48 hours.",
      firstName: "First Name",
      familyName: "Family Name",
      email: "Email",
      country: "Country",
      phone: "Phone Number",
      scope: "Venture Idea",
      budget: "Budget Range",
      message: "Message",
      scopePlaceholder: "e.g. A logistics SaaS",
      messagePlaceholder: "Tell us where you are, where you want to be, and anything else that matters.",
      budgetPlaceholder: "Select budget range",
      budgets: ["$5k – $15k", "$15k – $50k", "$50k – $150k", "$150k+", "Equity / Partnership model"],
      polish: "Polish with AI",
      polishing: "Polishing…",
      submit: "Dispatch Enquiry",
      sending: "Sending enquiry…",
      note: "Delivered straight to contact@multigroup.network",
      countryPlaceholder: "Select country…",
      countrySearch: "Search country…",
      noCountry: "No country found",
      dialCode: "Code",
      dialSearch: "Search code or country…",
      noCode: "No code found",
      errFill: "Please fill in all fields.",
      errEmail: "Please enter a valid email address.",
      errIdea: "Write a sentence about your idea first.",
      okSent: "Enquiry sent — it landed in our inbox at contact@multigroup.network.",
      okPolish: "Brief polished — feel free to edit it.",
      errSend: "Could not send your enquiry. Please email us directly.",
      errPolish: "AI polish is unavailable right now.",
    },
    footer: {
      brand: "Multigroupnetwork — an A-to-Z online venture builder & management studio.",
      slogan: "Your next step in human evolution",
      status: "All systems operational",
      index: "Index",
      contact: "Contact",
      location: "French company • Based in Toulouse, operating worldwide",
      hoursWeek: "Mon – Fri · 9:00 – 17:00",
      hoursWeekend: "Sat – Sun · Closed",
      backToTop: "Back to top",
      privacy: "Privacy",
      terms: "Terms",
      rights: "© 2026 Multigroupnetwork · Toulouse, France. All rights reserved.",
    },
    chat: {
      title: "Multigroupnetwork Assistant",
      modelTag: "AI • GPT 5.4 Mini",
      greeting: "Hi — I'm the Multigroupnetwork assistant. Ask me anything about how we build companies from A to Z.",
      suggestions: ["What does Multigroupnetwork do?", "How long does a build take?", "Who owns the IP after launch?"],
      placeholder: "Ask about building your company…",
      note: "AI answers about Multigroupnetwork • For details email contact@multigroup.network",
      error: "Sorry — I couldn't reach the server. Try again, or email contact@multigroup.network.",
      openAria: "Open Multigroupnetwork assistant",
      sendAria: "Send message",
    },
    case: {
      back: "Back to overview",
      badge: "[ Flagship Venture / Case Study ]",
      desc: "A smart shipping platform that turns travelers' spare luggage space into a global delivery network — conceived, built, published, and operated end-to-end by Multigroupnetwork.",
      socialsLabel: "Follow the SendexPro journey",
      heroImgAlt: "SendexPro — Send Anything, Trust Everyone",
      meta: [
        { label: "Sector", value: "Logistics / Crowd Shipping" },
        { label: "Model", value: "Multigroupnetwork Build & Operate" },
        { label: "Status", value: "In build" },
        { label: "Launch", value: "May 2027" },
      ],
      storyBadge: "[ The Story ]",
      storyHeading: "From a Whiteboard Sketch to a Global Network",
      chapterLabel: "Chapter",
      chapters: [
        { num: "01", title: "The Signal", body: "Millions of travelers fly with half-empty luggage while parcels wait weeks for affordable shipping. SendexPro is built on that observation. Multigroupnetwork validated the crowd-shipping market, mapped regulations across corridors, and sized the opportunity before a single screen was designed." },
        { num: "02", title: "The Build", body: "Happening right now: we are designing the SendexPro identity and engineering the full platform — sender and traveler onboarding, AI photo scanning for every item, route matching, dual-escrow payments, and a 4-digit PIN hand-off, all cloud-native and built to scale." },
        { num: "03", title: "The Launch", body: "SendexPro will be published 100% online at sendexpro.com with analytics, support tooling, and a launch campaign wired from day one. The first matched deliveries are planned for the opening week." },
        { num: "04", title: "The Operation", body: "After launch, Multigroupnetwork will keep operating SendexPro: infrastructure, verification reviews, growth experiments, and metric reporting — run by the same team that built it." },
      ],
      galleryBadge: "[ Product ]",
      galleryHeading: "Inside the Platform",
      gallery: [
        { caption: "Live dashboard — post an item or list a trip" },
        { caption: "Secure hand-off — dual-escrow + 4-digit PIN" },
      ],
      quote: "\u201CMultigroupnetwork helped us with the idea from A to Z — they didn't just build it, they run it with us.\u201D",
      quoteBy: "Founder of SendexPro",
      ctaBadge: "[ Your Turn ]",
      ctaHeading: "Want results like these for your idea?",
      ctaBody: "Multigroupnetwork builds and operates online companies from A to Z. SendexPro was slot 01 — the next flagship could be yours.",
      ctaStart: "Start your build",
      ctaVisit: "Visit sendexpro.com",
      footerCase: "© 2026 Multigroupnetwork — Case Study: SendexPro",
      footerHome: "Back to Multigroupnetwork",
      closeAria: "Close",
    },
    privacy: {
      badge: "[ Legal / Privacy ]",
      title: "Privacy Policy",
      sub: "How Multigroupnetwork handles the information you share with us — in plain language, without the legal fog.",
      sectionLabel: "Section",
      back: "Back to overview",
      footer: "© 2026 Multigroupnetwork — Your next step in human evolution",
      sections: [
        { num: "01", title: "What we collect", body: "When you send us an enquiry, we collect only what you type into the form: your name, email address, project scope, budget range, and message. Like every website, our servers also keep basic technical logs such as browser type and visit time." },
        { num: "02", title: "How we use it", body: "Your details are used for one thing only: reading your enquiry and replying to you. We never sell, rent, or share your information with third parties for marketing. Ever." },
        { num: "03", title: "Storage & security", body: "Enquiries are stored in a secure database and delivered to our team inbox at contact@multigroup.network. We keep them only as long as needed to handle your project conversation." },
        { num: "04", title: "Your rights", body: "You can ask us at any time to view, correct, or delete the information we hold about you. Write to contact@multigroup.network and we will take care of it — Monday to Friday, 9:00–17:00." },
      ],
    },
    terms: {
      badge: "[ Legal / Terms ]",
      title: "Terms of Service",
      sub: "The ground rules for building a company with Multigroupnetwork — short, clear, and fair to both sides.",
      sectionLabel: "Section",
      back: "Back to overview",
      footer: "© 2026 Multigroupnetwork — Your next step in human evolution",
      sections: [
        { num: "01", title: "What we do", body: "Multigroupnetwork is a venture management studio. We design, build, publish, and operate online companies — from strategy and branding to engineering, launch, and long-term operations." },
        { num: "02", title: "Working together", body: "Every project starts with a written agreement covering scope, timeline, and the engagement model — fixed fee, hybrid, or equity partnership. No work begins before both sides sign." },
        { num: "03", title: "Intellectual property", body: "On fixed-fee projects, 100% of the code, brand assets, and IP transfer to you at launch. Partnership and equity ventures define shared ownership in the venture agreement before any work begins." },
        { num: "04", title: "Content on this site", body: "Figures, timelines, and case-study details shown on this website illustrate our operating model unless stated otherwise. Venture launch dates depend on scope and are confirmed in your agreement." },
        { num: "05", title: "Contact", body: "Questions about these terms? Write to contact@multigroup.network — we answer every message during opening hours, Monday to Friday, 9:00–17:00. Weekends we rest." },
      ],
    },
  },

  fr: {
    nav: {
      philosophy: "Philosophie",
      services: "Services",
      portfolio: "Portfolio",
      faq: "FAQ",
      startProject: "Démarrer un projet",
      status: "Première entreprise en construction",
    },
    intro: { lines: ["VOTRE PROCHAINE ÉTAPE", "DANS L'ÉVOLUTION HUMAINE."] },
    hero: {
      chip: "Multigroupnetwork • Venture Studio en Ligne",
      lines: ["Nous Créons & Exploitons", "Des Entreprises Digitales", "De Zéro au Lancement"],
      subtext:
        "Vous avez une idée d'entreprise\u00A0? Multigroupnetwork construit, lance et exploite des entreprises digitales, de zéro au lancement. Venture studio en ligne à Toulouse, France.",
      ctaExplore: "Explorer le portfolio",
      ctaInitiate: "Lancer un projet",
      cardLabel: "Multigroupnetwork / Studio",
      cardTitleBuild: "Venture 01 en construction",
      cardTitleLive: "Venture 01 en ligne",
      cardBodyBuild:
        "SendexPro — notre première entreprise — sera lancée dans sept mois. Pas de chiffres gonflés ici, simplement du travail en cours.",
      cardBodyLive:
        "SendexPro — notre première entreprise — est publiée et opérationnelle. Le slot 02 est en construction.",
      yourVenture: "Votre entreprise",
      openSlot: "Place libre",
      comingSoon: "Bientôt disponible",
      liveNow: "En ligne",
    },
    marquee: ["Stratégie", "Design d'Identité", "Dév Full-Stack", "Publication en Ligne", "Infra Cloud", "Gestion de Croissance"],
    manifesto: {
      badge: "[ 01 / Philosophie ]",
      heading: "Le Modèle Opérationnel Multigroupnetwork",
      sub: "Nous ne sommes pas une agence. Nous sommes une société de gestion qui construit, publie et exploite son propre portefeuille d'entreprises en ligne — et qui construit les vôtres exactement de la même manière.",
      chapterLabel: "Chapitre",
      chapters: [
        { num: "01", title: "L'État Zéro", body: "Chaque entreprise naît d'un signal dans le bruit. Nous validons les marchés numériques inexploités avant d'écrire la moindre ligne de code." },
        { num: "02", title: "Architecture de Précision", body: "Nous concevons une infrastructure évolutive, cloud-native, capable de porter une entreprise de son premier utilisateur à son millionième." },
        { num: "03", title: "Lancement Souverain", body: "Le déploiement est une discipline. Nous publions des entreprises 100\u00A0% en ligne — sans frais physiques, sans plafond géographique." },
        { num: "04", title: "Croissance Perpétuelle", body: "Le lancement n'est que le premier jour. Multigroupnetwork exploite chaque entreprise avec des systèmes autonomes et une croissance pilotée par les métriques, indéfiniment." },
      ],
    },
    services: {
      badge: "[ 02 / Services ]",
      heading: "Création d'Entreprise sur Tout le Cycle de Vie",
      sub: "Un seul partenaire, cinq étapes. Vous apportez l'ambition — Multigroupnetwork livre une entreprise en ligne publiée et opérationnelle.",
      stageLabel: "Étape",
      steps: [
        { num: "01", title: "Stratégie & Intelligence de Marché", items: ["Validation du marché", "Cartographie concurrentielle", "Conception du modèle de revenus", "Feuille de route de l'entreprise"] },
        { num: "02", title: "Identité de Marque & Design Produit", items: ["Nom & logotype", "Système d'identité visuelle", "Architecture UX", "Design d'interface produit"] },
        { num: "03", title: "Ingénierie Full-Stack & IA", items: ["Développement de la plateforme web", "Intégration de l'IA", "Infrastructure cloud", "Sécurité & conformité"] },
        { num: "04", title: "Publication & Déploiement en Ligne", items: ["Déploiement en production", "Configuration domaine & DNS", "Branchement des analytics", "Campagne de lancement"] },
        { num: "05", title: "Exploitation & Croissance", items: ["Opérations gérées", "Expériences de croissance", "Rapports de métriques", "Passation à votre équipe"] },
      ],
      ventureLabel: "Votre Entreprise",
      ctaTitle: "Prêt à passer de l'idée à une entreprise opérationnelle\u00A0?",
      ctaBody: "Dites-nous ce que vous voulez construire. Nous répondons à chaque demande sous 48 heures.",
      ctaLink: "Démarrer la conversation",
    },
    portfolio: {
      badge: "[ 03 / Entreprises ]",
      heading: "Des Entreprises Construites & Gérées par Multigroupnetwork",
      sub: "Notre première entreprise sera bientôt lancée — avec d'autres sociétés en construction derrière elle.",
      projectDesc: "Plateforme d'expédition intelligente qui transforme l'espace libre dans les bagages des voyageurs en un réseau mondial de livraison.",
      tags: ["Crowd Shipping", "Logistique", "Marketplace"],
      comingSoon: "Bientôt disponible",
      launchesIn: " — lancement dans 7 mois",
      liveBadge: "En ligne — lancée",
      liveStripTitle: "Nous sommes en ligne.",
      liveStripNote: "SendexPro est publiée et opérationnelle",
      countdownLabels: ["Jours", "Heures", "Min", "Sec"],
      untilLaunch: "Avant le lancement",
      readCase: "Lire l'étude de cas",
      readCaseAria: "Lire l'étude de cas SendexPro",
      slotLabel: "Slot",
      haveIdea: "Une idée en tête\u00A0?",
      comingSoonPh: "Bientôt disponible",
      couldBeYou: "Ce pourrait être vous",
      inBuild: "Entreprise en construction",
      claimSpot: "Réservez votre place",
    },
    faq: {
      badge: "[ 04 / FAQ ]",
      heading: "Des Réponses Claires sur la Création avec Multigroupnetwork",
      sub: "Tout ce que les fondateurs demandent avant de lancer une création de A à Z. Une autre question en tête\u00A0? Écrivez-nous ci-dessous.",
      items: [
        { q: "Combien de temps faut-il pour construire et publier une entreprise avec Multigroupnetwork\u00A0?", a: "La plupart des entreprises passent d'une idée validée à une société en ligne publiée en 4 à 8 semaines, selon l'ampleur du projet. Les produits SaaS simples se rapprochent de 4 semaines\u00A0; les plateformes avec IA ou infrastructure complexe demandent généralement 6 à 8." },
        { q: "Travaillez-vous au forfait ou au capital\u00A0?", a: "Les deux. Nous proposons un modèle de création au forfait, un partenariat hybride mêlant forfait et participation, et — pour les idées exceptionnelles — un modèle de partenariat complet où Multigroupnetwork investit la création en échange de parts. Nous vous aidons à choisir la structure qui vous convient." },
        { q: "À qui appartiennent l'entreprise et la propriété intellectuelle\u00A0?", a: "À vous. Dans le cadre d'un forfait, 100\u00A0% de la propriété intellectuelle, du code, des éléments de marque et de l'infrastructure vous sont transférés au lancement. Les partenariats en capital définissent un partage de propriété transparent dans le pacte de venture, avant le début de tout travail." },
        { q: "Que se passe-t-il après le lancement\u00A0? Multigroupnetwork peut-elle continuer à exploiter l'entreprise\u00A0?", a: "Oui — c'est ce qui fait de Multigroupnetwork une société de gestion plutôt qu'une agence. Nous proposons une exploitation continue\u00A0: gestion de l'infrastructure, expériences de croissance, rapports de métriques et itérations produit. Vous pouvez aussi tout reprendre grâce à une passation structurée." },
        { q: "Sur quelle stack technologique construisez-vous\u00A0?", a: "Nous choisissons la stack en fonction de chaque entreprise, pas par habitude. Nos réalisations utilisent des frameworks web modernes, une infrastructure cloud-native et des intégrations d'IA lorsqu'elles créent un réel levier. Tout est documenté et conçu pour être repris par votre équipe." },
      ],
    },
    contact: {
      badge: "[ 05 / Contact ]",
      heading: "Construisez Votre Prochaine Entreprise Digitale avec Multigroupnetwork",
      sub: "Parlez-nous de l'entreprise que vous voulez construire. Chaque demande est lue par un associé, et nous répondons sous 48 heures.",
      firstName: "Prénom",
      familyName: "Nom de famille",
      email: "E-mail",
      country: "Pays",
      phone: "Numéro de téléphone",
      scope: "Idée d'entreprise",
      budget: "Budget",
      message: "Message",
      scopePlaceholder: "ex. une SaaS logistique",
      messagePlaceholder: "Dites-nous où vous en êtes, où vous voulez aller, et tout ce qui compte.",
      budgetPlaceholder: "Sélectionnez une fourchette de budget",
      budgets: ["5 k$ – 15 k$", "15 k$ – 50 k$", "50 k$ – 150 k$", "150 k$ et plus", "Modèle au capital / partenariat"],
      polish: "Polir avec l'IA",
      polishing: "Polissage…",
      submit: "Envoyer la demande",
      sending: "Envoi en cours…",
      note: "Transmis directement à contact@multigroup.network",
      countryPlaceholder: "Sélectionnez un pays…",
      countrySearch: "Rechercher un pays…",
      noCountry: "Aucun pays trouvé",
      dialCode: "Indicatif",
      dialSearch: "Rechercher un indicatif ou un pays…",
      noCode: "Aucun indicatif trouvé",
      errFill: "Veuillez remplir tous les champs.",
      errEmail: "Veuillez saisir une adresse e-mail valide.",
      errIdea: "Écrivez d'abord une phrase sur votre idée.",
      okSent: "Demande envoyée — elle est bien arrivée dans notre boîte contact@multigroup.network.",
      okPolish: "Résumé poli — vous pouvez le modifier librement.",
      errSend: "Impossible d'envoyer votre demande. Écrivez-nous directement par e-mail.",
      errPolish: "Le polissage IA est indisponible pour le moment.",
    },
    footer: {
      brand: "Multigroupnetwork — créateur & société de gestion d'entreprises en ligne, de A à Z.",
      slogan: "Votre prochaine étape dans l'évolution humaine",
      status: "Tous les systèmes opérationnels",
      index: "Index",
      contact: "Contact",
      location: "Entreprise française • Basée à Toulouse, active dans le monde entier",
      hoursWeek: "Lun – Ven · 9:00 – 17:00",
      hoursWeekend: "Sam – Dim · Fermé",
      backToTop: "Retour en haut",
      privacy: "Confidentialité",
      terms: "Conditions",
      rights: "© 2026 Multigroupnetwork · Toulouse, France. Tous droits réservés.",
    },
    chat: {
      title: "Assistant Multigroupnetwork",
      modelTag: "IA • GPT 5.4 Mini",
      greeting: "Bonjour — je suis l'assistant Multigroupnetwork. Posez-moi toutes vos questions sur la façon dont nous construisons des entreprises de A à Z.",
      suggestions: ["Que fait Multigroupnetwork\u00A0?", "Combien de temps prend une création\u00A0?", "À qui appartient la propriété intellectuelle\u00A0?"],
      placeholder: "Posez une question sur votre projet d'entreprise…",
      note: "Réponses IA sur Multigroupnetwork • Pour plus de détails, écrivez à contact@multigroup.network",
      error: "Désolé — je n'arrive pas à joindre le serveur. Réessayez, ou écrivez à contact@multigroup.network.",
      openAria: "Ouvrir l'assistant Multigroupnetwork",
      sendAria: "Envoyer le message",
    },
    case: {
      back: "Retour à l'aperçu",
      badge: "[ Entreprise Phare / Étude de Cas ]",
      desc: "Une plateforme d'expédition intelligente qui transforme l'espace libre dans les bagages des voyageurs en un réseau mondial de livraison — conçue, construite, publiée et exploitée de bout en bout par Multigroupnetwork.",
      socialsLabel: "Suivez l'aventure SendexPro",
      heroImgAlt: "SendexPro — Envoyez n'importe quoi, en toute confiance",
      meta: [
        { label: "Secteur", value: "Logistique / Crowd Shipping" },
        { label: "Modèle", value: "Création & Exploitation Multigroupnetwork" },
        { label: "Statut", value: "En construction" },
        { label: "Lancement", value: "Mai 2027" },
      ],
      storyBadge: "[ L'Histoire ]",
      storyHeading: "D'un Croquis sur Tableau Blanc à un Réseau Mondial",
      chapterLabel: "Chapitre",
      chapters: [
        { num: "01", title: "Le Signal", body: "Des millions de voyageurs volent avec des bagages à moitié vides pendant que des colis attendent des semaines une expédition abordable. SendexPro est née de cette observation. Multigroupnetwork a validé le marché du crowd-shipping, cartographié les réglementations sur les corridors et évalué l'opportunité avant de dessiner le moindre écran." },
        { num: "02", title: "La Création", body: "C'est en cours en ce moment même\u00A0: nous dessinons l'identité de SendexPro et développons l'ensemble de la plateforme — inscription des expéditeurs et des voyageurs, scan photo par IA pour chaque objet, mise en relation des trajets, paiements en double séquestre et remise par code PIN à 4 chiffres, le tout cloud-native et conçu pour évoluer." },
        { num: "03", title: "Le Lancement", body: "SendexPro sera publiée 100\u00A0% en ligne sur sendexpro.com, avec analytics, outils de support et campagne de lancement branchés dès le premier jour. Les premières livraisons mises en relation sont prévues dès la semaine d'ouverture." },
        { num: "04", title: "L'Exploitation", body: "Après le lancement, Multigroupnetwork continuera d'exploiter SendexPro\u00A0: infrastructure, contrôles de vérification, expériences de croissance et rapports de métriques — gérés par la même équipe qui l'a construite." },
      ],
      galleryBadge: "[ Produit ]",
      galleryHeading: "À l'Intérieur de la Plateforme",
      gallery: [
        { caption: "Tableau de bord en direct — publiez un objet ou proposez un trajet" },
        { caption: "Remise sécurisée — double séquestre + code PIN à 4 chiffres" },
      ],
      quote: "«\u00A0Multigroupnetwork nous a accompagnés sur l'idée de A à Z — ils ne se sont pas contentés de la construire, ils l'exploitent avec nous.\u00A0»",
      quoteBy: "Fondateur de SendexPro",
      ctaBadge: "[ À Vous ]",
      ctaHeading: "Vous voulez des résultats comme ceux-ci pour votre idée\u00A0?",
      ctaBody: "Multigroupnetwork construit et exploite des entreprises en ligne de A à Z. SendexPro occupait le slot 01 — la prochaine entreprise phare pourrait être la vôtre.",
      ctaStart: "Lancez votre projet",
      ctaVisit: "Visiter sendexpro.com",
      footerCase: "© 2026 Multigroupnetwork — Étude de cas\u00A0: SendexPro",
      footerHome: "Retour à Multigroupnetwork",
      closeAria: "Fermer",
    },
    privacy: {
      badge: "[ Légal / Confidentialité ]",
      title: "Politique de Confidentialité",
      sub: "Comment Multigroupnetwork traite les informations que vous nous confiez — en langage clair, sans brouillard juridique.",
      sectionLabel: "Section",
      back: "Retour à l'aperçu",
      footer: "© 2026 Multigroupnetwork — Votre prochaine étape dans l'évolution humaine",
      sections: [
        { num: "01", title: "Ce que nous collectons", body: "Lorsque vous nous envoyez une demande, nous collectons uniquement ce que vous saisissez dans le formulaire\u00A0: votre nom, votre adresse e-mail, le périmètre de votre projet, votre fourchette de budget et votre message. Comme tout site web, nos serveurs conservent également des journaux techniques de base, comme le type de navigateur et l'heure de visite." },
        { num: "02", title: "Comment nous les utilisons", body: "Vos informations servent à une seule chose\u00A0: lire votre demande et vous répondre. Nous ne vendons, ne louons ni ne partageons jamais vos informations avec des tiers à des fins marketing. Jamais." },
        { num: "03", title: "Stockage & sécurité", body: "Les demandes sont stockées dans une base de données sécurisée et transmises à la boîte de notre équipe, à contact@multigroup.network. Nous les conservons uniquement le temps nécessaire pour mener la conversation sur votre projet." },
        { num: "04", title: "Vos droits", body: "Vous pouvez nous demander à tout moment de consulter, corriger ou supprimer les informations que nous détenons sur vous. Écrivez à contact@multigroup.network et nous nous en occupons — du lundi au vendredi, de 9 h à 17 h." },
      ],
    },
    terms: {
      badge: "[ Légal / Conditions ]",
      title: "Conditions d'Utilisation",
      sub: "Les règles du jeu pour construire une entreprise avec Multigroupnetwork — courtes, claires et équitables pour les deux parties.",
      sectionLabel: "Section",
      back: "Retour à l'aperçu",
      footer: "© 2026 Multigroupnetwork — Votre prochaine étape dans l'évolution humaine",
      sections: [
        { num: "01", title: "Ce que nous faisons", body: "Multigroupnetwork est un studio de gestion d'entreprises. Nous concevons, construisons, publions et exploitons des entreprises en ligne — de la stratégie et de la marque jusqu'à l'ingénierie, au lancement et à l'exploitation à long terme." },
        { num: "02", title: "Travailler ensemble", body: "Chaque projet commence par un accord écrit couvrant le périmètre, le calendrier et le modèle d'engagement — forfait, hybride ou partenariat en capital. Aucun travail ne commence avant la signature des deux parties." },
        { num: "03", title: "Propriété intellectuelle", body: "Pour les projets au forfait, 100\u00A0% du code, des éléments de marque et de la propriété intellectuelle vous sont transférés au lancement. Les entreprises en partenariat ou en capital définissent le partage de propriété dans le pacte de venture, avant le début de tout travail." },
        { num: "04", title: "Contenu de ce site", body: "Les chiffres, calendriers et détails d'études de cas présentés sur ce site illustrent notre modèle opérationnel, sauf mention contraire. Les dates de lancement des entreprises dépendent du périmètre et sont confirmées dans votre accord." },
        { num: "05", title: "Contact", body: "Des questions sur ces conditions\u00A0? Écrivez à contact@multigroup.network — nous répondons à chaque message pendant les heures d'ouverture, du lundi au vendredi, de 9 h à 17 h. Le week-end, nous nous reposons." },
      ],
    },
  },
};
